import { ISelectOption } from '@/components/form/fields/SelectField';
import { axiosInstance } from '@/utils/libs/axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

interface ITestIssueItem {
  id: string;
  title: string;
  code_issue: string;
}

export const linkTestIssueSchema = z.object({
  test_run_detail_id: z.string().optional(),
  test_issue_id: z.array(z.string()),
});

export const detailTestRunIssueSchema = z.object({
  id: z.string().optional(),
  test_run_detail_id: z.string().optional(),
  test_issue_id: z.string(),
});

export type ITestIssueOption = {
  testIssue: ISelectOption[];
};

export type IDetailTestRunIssue = z.infer<typeof detailTestRunIssueSchema> & {
  test_run_detail_issue: IDetailTestRunIssue;
};

export type ILinkTestIssue = z.infer<typeof linkTestIssueSchema>;

export const TestRunDetailIssueService = {
  useGetAll: () =>
    useQuery<IDetailTestRunIssue>({
      queryKey: ['test-run-detail-issue'],
      queryFn: async () => {
        const response = await axiosInstance.get(`/api/crud/test-run-detail`, {
          params: {
            filter: {
              test_issue_id: 'test_issue_id',
            },
          },
        });

        return response.data?.data;
      },
    }),

  useLink: () =>
    useMutation({
      mutationFn: async (data: ILinkTestIssue) => {
        await axiosInstance.post(
          `/api/crud/test-run-detail/${data.test_run_detail_id}/link`,
          data,
        );
      },
    }),

  useGetTestIssue: (projectId?: string) => {
    return useQuery<ITestIssueOption>({
      queryKey: ['test-issue'],
      queryFn: async () => {
        const response = await axiosInstance.get('/api/crud/test-issue/all', {
          params: { filter: { project_id: projectId } },
        });

        const testIssueOption = response.data.data.map(
          (item: ITestIssueItem) => ({
            value: item.id,
            label: `${item.code_issue} - ${item.title}`,
          }),
        );

        return {
          testIssue: testIssueOption ?? [],
        };
      },
    });
  },
  useClearLink: () =>
    useMutation({
      mutationFn: async (data: ILinkTestIssue) => {
        await axiosInstance.post(
          `/api/crud/test-run-detail/${data.test_run_detail_id}/clear-link`,
          data,
        );
      },
    }),
};
